import React, { useEffect, useState } from "react";
import { CheckCircle, CircleNotch } from "@phosphor-icons/react";

/**
 * AnalysisProgress
 * Animated multi-stage progress display. Because the actual LLM call is a single
 * HTTP request (no real streaming events), we drive the stages on a schedule that
 * matches the typical Catalog Analyzer timing (8–14s end-to-end). When the parent
 * passes `done=true` we jump to the final state. If the API takes longer than
 * expected the last stage pulses indefinitely so the user knows we're still working.
 *
 * Usage:
 *   <AnalysisProgress active={loading} done={!!result} stages={[…]} />
 */
export default function AnalysisProgress({ active, done, stages, label = "Analysis" }) {
  const [idx, setIdx] = useState(0);
  const [pct, setPct] = useState(0);

  useEffect(() => {
    if (!active) { setIdx(0); setPct(0); return; }
    let cancelled = false;
    const totalMs = stages.reduce((a, s) => a + (s.ms || 1500), 0);
    let elapsed = 0;
    let acc = 0;
    const tick = () => {
      if (cancelled) return;
      elapsed += 120;
      // Smoothly grow percentage but never reach 100 until parent signals done.
      const cap = done ? 100 : 95;
      const target = Math.min(cap, (elapsed / totalMs) * 100);
      setPct((p) => Math.max(p, target));
      // Stage advance based on elapsed vs cumulative stage ms.
      acc = 0;
      let nextIdx = stages.length - 1;
      for (let i = 0; i < stages.length; i++) {
        acc += stages[i].ms || 1500;
        if (elapsed < acc) { nextIdx = i; break; }
      }
      setIdx(nextIdx);
      if (!done) setTimeout(tick, 120);
    };
    const t = setTimeout(tick, 120);
    return () => { cancelled = true; clearTimeout(t); };
  }, [active, done, stages]);

  useEffect(() => {
    if (done) { setIdx(stages.length - 1); setPct(100); }
  }, [done, stages.length]);

  if (!active && !done) return null;

  return (
    <div className="mt-6 border border-ink bg-white" data-testid="analysis-progress">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-rule px-5 py-3">
        <div className="flex items-center gap-3">
          {done ? (
            <CheckCircle weight="fill" className="w-5 h-5 text-signal" />
          ) : (
            <CircleNotch weight="bold" className="w-5 h-5 text-persimmon animate-spin" />
          )}
          <span className="label-mono">
            {label} · {done ? "Complete" : "In progress"}
          </span>
        </div>
        <span className="font-mono tabular text-sm text-ink">
          {Math.round(pct)}<span className="text-ink/30">%</span>
        </span>
      </div>

      {/* Segmented progress bar — 40 thin slabs */}
      <div className="grid grid-cols-[repeat(40,1fr)] gap-[2px] px-5 py-4 bg-paper">
        {Array.from({ length: 40 }).map((_, i) => {
          const lit = i < Math.round((pct / 100) * 40);
          return (
            <span
              key={`bar-${i}`}
              className={`h-3 transition-colors duration-200 ${
                lit ? (done ? "bg-signal" : "bg-persimmon") : "bg-rule"
              }`}
            />
          );
        })}
      </div>

      {/* Stage ticker */}
      <ol className="divide-y divide-rule">
        {stages.map((s, i) => {
          const status = done || i < idx ? "done" : i === idx ? "active" : "pending";
          return (
            <li key={s.title} className="flex items-center gap-4 px-5 py-3" data-testid={`stage-${i}-${status}`}>
              <span className={`font-mono tabular text-xs w-10 ${
                status === "done" ? "text-signal" :
                status === "active" ? "text-persimmon" : "text-ink/30"
              }`}>
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="w-5 h-5 grid place-items-center shrink-0">
                {status === "done" && <CheckCircle weight="fill" className="w-5 h-5 text-signal" />}
                {status === "active" && <CircleNotch weight="bold" className="w-5 h-5 text-persimmon animate-spin" />}
                {status === "pending" && <span className="w-2 h-2 bg-rule" />}
              </span>
              <span className={`text-sm ${
                status === "done" ? "text-ink/50 line-through decoration-ink/20" :
                status === "active" ? "text-ink font-medium" :
                "text-ink/40"
              }`}>
                {s.title}
              </span>
              {status === "active" && s.detail && (
                <span className="ml-auto label-mono text-ink/40">{s.detail}</span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
